import { useEffect, useRef } from 'react';
import { addDays, toDateString, isToday } from '@/lib/dates';
import type { CalendarEvent } from '@/types';
import styles from './WeekView.module.css';

const DAYS_BEFORE = 3;   // 오늘 기준 왼쪽에 보여줄 과거 일수
const DAYS_TOTAL = 120;  // 전체 렌더링 일수
const DAYS_VISIBLE = 3;  // 한 화면에 보이는 열 수

const COLOR_MAP: Record<string, string> = {
  blue: 'var(--event-blue)',
  purple: 'var(--event-purple)',
  green: 'var(--event-green)',
  orange: 'var(--event-orange)',
  pink: 'var(--event-pink)',
  cyan: 'var(--event-cyan)',
};

const DOW = ['일', '월', '화', '수', '목', '금', '토'];

interface Props {
  selectedDate: Date;
  events: CalendarEvent[];
  onSelectDay: (date: Date) => void;
  onEventClick: (event: CalendarEvent) => void;
  onDateChange?: (date: Date) => void;
  onPrevWeek?: () => void;
  onNextWeek?: () => void;
}

export function WeekView({ selectedDate, events, onSelectDay, onEventClick, onDateChange }: Props) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const lastScrolledKey = useRef('');
  const scrollTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  // baseDate와 allDays는 마운트 시 한 번만 계산 (오늘 기준)
  const baseDateRef = useRef<Date | null>(null);
  const allDaysRef = useRef<Date[]>([]);
  if (!baseDateRef.current) {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    baseDateRef.current = addDays(d, -DAYS_BEFORE);
    allDaysRef.current = Array.from(
      { length: DAYS_TOTAL },
      (_, i) => addDays(baseDateRef.current!, i),
    );
  }

  function colWidth() {
    return scrollRef.current ? scrollRef.current.offsetWidth / DAYS_VISIBLE : 0;
  }

  function scrollToDate(date: Date, behavior: ScrollBehavior) {
    if (!scrollRef.current) return;
    const diff = Math.round(
      (date.getTime() - baseDateRef.current!.getTime()) / 86400000,
    );
    const left = Math.max(0, diff) * colWidth();
    scrollRef.current.scrollTo({ left, behavior });
  }

  // 마운트: 오늘(또는 selectedDate)로 즉시 이동
  useEffect(() => {
    scrollToDate(selectedDate, 'instant');
    lastScrolledKey.current = toDateString(selectedDate);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // selectedDate가 외부(헤더 버튼 등)에서 바뀌면 smooth 스크롤
  useEffect(() => {
    const key = toDateString(selectedDate);
    if (key === lastScrolledKey.current) return;
    lastScrolledKey.current = key;
    scrollToDate(selectedDate, 'smooth');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDate]);

  // 스크롤이 멈추면 현재 위치를 selectedDate로 업데이트
  function handleScroll() {
    if (scrollTimer.current) clearTimeout(scrollTimer.current);
    scrollTimer.current = setTimeout(() => {
      if (!scrollRef.current) return;
      const idx = Math.round(scrollRef.current.scrollLeft / colWidth());
      const clamped = Math.max(0, Math.min(idx, allDaysRef.current.length - 1));
      const date = allDaysRef.current[clamped];
      if (date) {
        const key = toDateString(date);
        lastScrolledKey.current = key;
        onDateChange?.(date);
      }
    }, 80);
  }

  return (
    <div className={styles.weekView}>
      <div
        ref={scrollRef}
        className={styles.scrollContainer}
        onScroll={handleScroll}
      >
        {allDaysRef.current.map((day, i) => {
          const ds = toDateString(day);
          const dayEvents = events.filter(e => e.date === ds);
          const allDayEvts = dayEvents.filter(e => e.isAllDay || !e.startTime);
          const timed = dayEvents
            .filter(e => !e.isAllDay && e.startTime)
            .sort((a, b) => (a.startTime || '').localeCompare(b.startTime || ''));
          const isTodayDay = isToday(day);

          return (
            <div
              key={i}
              className={`${styles.dayColumn} ${isTodayDay ? styles.todayColumn : ''}`}
              onClick={() => onSelectDay(day)}
            >
              <div className={styles.dayHeader}>
                <span className={`${styles.dayName} ${isTodayDay ? styles.todayDayName : ''}`}>
                  {DOW[day.getDay()]}
                </span>
                <span className={`${styles.dayNum} ${isTodayDay ? styles.todayNum : ''}`}>
                  {day.getDate()}
                </span>
              </div>

              <div className={styles.eventList}>
                {allDayEvts.map(e => (
                  <button
                    key={e.id}
                    className={styles.eventCard}
                    style={{ borderLeft: `3px solid ${COLOR_MAP[e.color] || COLOR_MAP.blue}` }}
                    onClick={ev => { ev.stopPropagation(); onEventClick(e); }}
                  >
                    <span className={styles.cardTitle}>{e.title}</span>
                    <span className={styles.allDayBadge}>종일</span>
                  </button>
                ))}
                {timed.map(e => (
                  <button
                    key={e.id}
                    className={styles.eventCard}
                    style={{ borderLeft: `3px solid ${COLOR_MAP[e.color] || COLOR_MAP.blue}` }}
                    onClick={ev => { ev.stopPropagation(); onEventClick(e); }}
                  >
                    <span className={styles.cardTime}>{e.startTime}</span>
                    <span className={styles.cardTitle}>{e.title}</span>
                  </button>
                ))}
                {dayEvents.length === 0 && (
                  <div className={styles.emptyMsg}>—</div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
